import {
  EventMarket,
  EventOutcome,
  EventResponse,
  EventType,
  Market,
  Outcome,
  OutcomeItem,
} from '@/types';
import { formatProbability } from './formatters';

import { UMA_RESOLUTION_STATUS } from '@/constants/market.constants';

export function isMarketResolved(market: OutcomeItem | undefined): boolean {
  if (!market) return false;

  // Type guard approach or direct property check
  if ('umaResolutionStatus' in market) {
    return (market as EventMarket).umaResolutionStatus === UMA_RESOLUTION_STATUS.RESOLVED;
  }

  if ('status' in market) {
    return (market as Market).status === 'resolved';
  }

  return false;
}

/**
 * Check if ALL markets in an event are resolved
 */
export function areAllMarketsResolved(event: EventResponse | undefined | null): boolean {
  if (!event || !event.markets || event.markets.length === 0) return false;
  return event.markets.every((m) => isMarketResolved(m));
}

/**
 * Find the resolved outcome of a market or event
 */
export function findResolvedOutcome(event: EventResponse | undefined | null, market: EventMarket) {
  if (!event) return null;
  const isMultiOutcome = event.negRisk === true || (event.markets && event.markets.length > 2);

  if (isMultiOutcome && event.markets) {
    for (const m of event.markets) {
      let outcomesArray: EventOutcome[] = [];
      try {
        outcomesArray = typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : m.outcomes;
      } catch {
        continue;
      }

      // In Multi-Outcome (negRisk), we look for the market that resolved to "Yes"
      const win = outcomesArray.find(
        (o) => String(o.price) === '1' && o.name.toLowerCase() === 'yes'
      );
      if (win) {
        return {
          ...win,
          displayName: (m as any).displayName || m.groupItemTitle || m.question || win.name,
        };
      }
    }
  }

  let outcomesArray: EventOutcome[] = [];
  if (typeof market.outcomes === 'string') {
    try {
      outcomesArray = JSON.parse(market.outcomes);
    } catch (e) {
      return null;
    }
  } else if (Array.isArray(market.outcomes)) {
    outcomesArray = market.outcomes as EventOutcome[];
  }

  const win = outcomesArray.find((o) => String(o.price) === '1');
  if (win) {
    return {
      ...win,
      displayName: (win as any).displayName || win.name,
    };
  }

  return null;
}

/**
 * Determine the event type based on the EventResponse structure
 * Analyzes the markets array to classify the event
 */
export function getEventType(eventResponse: EventResponse | undefined | null): EventType {
  // Fallback if no event response
  if (!eventResponse) {
    return 'SIMPLE_BINARY';
  }

  // Check for sportsCard
  if (eventResponse.gameId) {
    return 'SPORTS_CARD';
  }

  // Check for negative risk events (multi-outcome, winner-take-all)
  if (eventResponse.negRisk === true) {
    return 'MULTI_OUTCOME';
  }

  // Filter out negRiskOther markets (placeholder "Other" outcomes) when determining count
  const tradableMarkets = eventResponse.markets.filter((m) => !m.negRiskOther);

  const marketsCount = tradableMarkets.length || 0;

  // Fallback if no markets
  if (marketsCount === 0) {
    return 'SIMPLE_BINARY';
  }

  const firstMarket = tradableMarkets[0];
  let outcomeNames: string[] = [];

  try {
    let parsed: any;
    if (typeof firstMarket.outcomes === 'string') {
      parsed = JSON.parse(firstMarket.outcomes);
    } else {
      parsed = firstMarket.outcomes;
    }

    if (Array.isArray(parsed)) {
      outcomeNames = parsed
        .map((outcome: string | EventOutcome) => {
          if (typeof outcome === 'string') {
            return outcome;
          } else if (outcome && typeof outcome === 'object' && 'name' in outcome) {
            return outcome.name;
          }
          return '';
        })
        .filter(Boolean);
    }
  } catch (e) {
    console.warn('Failed to parse outcomes from market:', e);
    return 'SIMPLE_BINARY';
  }

  const hasYesNo = outcomeNames.some(
    (name) => name.toLowerCase() === 'yes' || name.toLowerCase() === 'no'
  );

  if (marketsCount >= 3) {
    return 'MULTI_OUTCOME';
  }

  if (marketsCount === 1 && outcomeNames.length > 2 && !hasYesNo) {
    return 'MULTI_OUTCOME';
  }

  if (marketsCount >= 2 && hasYesNo) {
    return 'MULTI_QUESTION';
  }

  if (marketsCount === 1 && !hasYesNo && outcomeNames.length === 2) {
    return 'CUSTOM_BINARY';
  }

  return 'SIMPLE_BINARY';
}

/**
 * Parse outcomes from a market's outcomes array or string
 */
export function parseMarketOutcomes(outcomes: string | EventOutcome[]): string[] {
  try {
    let parsed: any;
    if (typeof outcomes === 'string') {
      parsed = JSON.parse(outcomes);
    } else {
      parsed = outcomes;
    }

    if (Array.isArray(parsed)) {
      return parsed
        .map((outcome: string | EventOutcome) => {
          if (typeof outcome === 'string') {
            return outcome;
          } else if (outcome && typeof outcome === 'object' && 'name' in outcome) {
            return outcome.name;
          }
          return '';
        })
        .filter(Boolean);
    }
    return [];
  } catch (e) {
    console.error('Failed to parse market outcomes:', e);
    return [];
  }
}

/**
 * Parse outcome prices from a market's outcomePrices array or string
 */
export function parseOutcomePrices(outcomePrices: string | string[] | undefined): string[] {
  if (!outcomePrices) return [];
  try {
    let parsed: any;
    if (typeof outcomePrices === 'string') {
      parsed = JSON.parse(outcomePrices);
    } else {
      parsed = outcomePrices;
    }

    if (Array.isArray(parsed)) {
      return parsed.map(String);
    }
    return [];
  } catch (e) {
    console.error('Failed to parse outcome prices:', e);
    return [];
  }
}

/**
 * Calculate the probability (percentage) of the first outcome
 */
export function getMarketProbability(market: OutcomeItem): string {
  const outcomes = market.outcomes;
  let firstOutcomePrice: string | number = 0;

  if (Array.isArray(outcomes) && outcomes.length > 0) {
    firstOutcomePrice = outcomes[0].price;
  } else if (typeof outcomes === 'string') {
    try {
      const parsed = JSON.parse(outcomes);
      if (Array.isArray(parsed) && parsed.length > 0) {
        firstOutcomePrice = typeof parsed[0] === 'string' ? parsed[0] : parsed[0].price || 0;
      }
    } catch (e) {
      // Fallback to outcomePrices if it exists
      if ((market as any).outcomePrices) {
        try {
          const prices = JSON.parse((market as any).outcomePrices);
          if (Array.isArray(prices) && prices.length > 0) {
            firstOutcomePrice = prices[0];
          }
        } catch (innerE) {
          // Ignore
        }
      }
    }
  }

  const priceNum =
    typeof firstOutcomePrice === 'string' ? parseFloat(firstOutcomePrice) : firstOutcomePrice;

  if (isNaN(priceNum) || priceNum === 0) return '0%';
  if (priceNum >= 1) return '100%';
  return formatProbability(priceNum, 0) + '%';
}

// Type guard to check if an item is a Market
export function isMarket(item: any): item is Market {
  return item && Array.isArray(item.outcomes) && !('price' in item) && !('volumeNum' in item);
}

// Type guard for EventMarket
export function isEventMarket(item: any): item is EventMarket {
  return item && 'volumeNum' in item;
}

// Helper to resolve display data from either Market or Outcome
export function resolveOutcomeData(item: OutcomeItem) {
  let id = item.id;
  let name = '';
  let price = 0;
  let priceNo = 0;
  let bestAsk = 0;
  let bestBid = 0;
  let bestAskNo = 0;
  let bestBidNo = 0;
  let volume = 0;
  let image: string | undefined;
  let outcomeNameYes = 'Yes';
  let outcomeNameNo = 'No';

  let active = true;
  let archived = false;

  const parseOutcomes = (outcomes: any): EventOutcome[] => {
    if (typeof outcomes === 'string') {
      try {
        return JSON.parse(outcomes);
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(outcomes) ? outcomes : [];
  };

  if (isEventMarket(item)) {
    // Handle EventMarket
    active = item.active;
    archived = item.archived;
    volume = item.volumeNum || Number(item.volume || 0);
    name = normalizeMarketQuestion(item);
    image = item.image;

    const outcomesArray = parseOutcomes(item.outcomes);

    if (outcomesArray.length > 0) {
      const yesOutcome = outcomesArray.find((o) => o?.name?.toLowerCase() === 'yes');
      const noOutcome = outcomesArray.find((o) => o?.name?.toLowerCase() === 'no');

      const primaryOutcome = yesOutcome || outcomesArray[0];
      const secondaryOutcome =
        noOutcome ||
        (outcomesArray.length >= 2
          ? primaryOutcome === outcomesArray[0]
            ? outcomesArray[1]
            : outcomesArray[0]
          : null);

      if (outcomesArray.length >= 1) outcomeNameYes = outcomesArray[0].name;
      if (outcomesArray.length >= 2) outcomeNameNo = outcomesArray[1].name;

      if (primaryOutcome) {
        id = primaryOutcome.id || item.id;
        price = Number(primaryOutcome.price || 0);
        bestAsk = Number(primaryOutcome.bestAsk || primaryOutcome.price || 0);
        bestBid = Number(primaryOutcome.bestBid || primaryOutcome.price || 0);
        if (name === 'Unknown') name = primaryOutcome.name;
      }
      if (secondaryOutcome) {
        priceNo = Number(secondaryOutcome.price || 0);
        bestAskNo = Number(secondaryOutcome.bestAsk || secondaryOutcome.price || 0);
        bestBidNo = Number(secondaryOutcome.bestBid || secondaryOutcome.price || 0);
      }
    }
  } else if (isMarket(item)) {
    const outcomesArray = parseOutcomes(item.outcomes);
    active = item.status !== 'closed' && item.status !== 'resolved';
    archived = (item as any).archived === true;

    if (outcomesArray.length > 0) {
      const yesOutcome = outcomesArray.find((o) => o?.name?.toLowerCase() === 'yes');
      const noOutcome = outcomesArray.find((o) => o?.name?.toLowerCase() === 'no');

      const primaryOutcome = yesOutcome || outcomesArray[0];
      const secondaryOutcome =
        noOutcome ||
        (outcomesArray.length >= 2
          ? primaryOutcome === outcomesArray[0]
            ? outcomesArray[1]
            : outcomesArray[0]
          : null);

      if (outcomesArray.length >= 1) outcomeNameYes = outcomesArray[0].name;
      if (outcomesArray.length >= 2) outcomeNameNo = outcomesArray[1].name;

      if (primaryOutcome) {
        id = primaryOutcome.id;
        price = Number(primaryOutcome.price || 0);
        bestAsk = Number((primaryOutcome as any).bestAsk || primaryOutcome.price || 0);
        bestBid = Number((primaryOutcome as any).bestBid || primaryOutcome.price || 0);
      }
      if (secondaryOutcome) {
        priceNo = Number(secondaryOutcome.price || 0);
        bestAskNo = Number((secondaryOutcome as any).bestAsk || secondaryOutcome.price || 0);
        bestBidNo = Number((secondaryOutcome as any).bestBid || secondaryOutcome.price || 0);
      }

      // Use market question/title if name is missing
      name = (item as any).groupItemTitle || item.question || primaryOutcome?.name || 'Unknown';
    } else {
      name = item.question || 'Unknown';
    }

    image = item.image;
    volume = Number((item as any).volumeNum || item.volume || 0);
  } else {
    // Handle Outcome
    name = (item as any).question || item.name || '';
    price = Number(item.price || 0);
    bestAsk = Number((item as any).bestAsk || item.price || 0);
    bestBid = Number((item as any).bestBid || item.price || 0);
    volume = Number((item as any).volumeNum || item.volume || 0);
    image = item.image;
    outcomeNameYes = item.name || 'Yes';
    active = (item as any).active !== false;
    archived = (item as any).archived === true;
    // For a single outcome, bestAskNo isn't explicitly defined, so we can use a fallback
    bestAskNo = 1 - bestAsk;
    bestBidNo = 1 - bestBid;
  }

  return {
    id,
    name,
    price,
    priceNo,
    bestAsk,
    bestBid,
    bestAskNo,
    bestBidNo,
    volume,
    image,
    outcomeNameYes,
    outcomeNameNo,
    active,
    archived,
  };
}

// Helper to get initials from name
export const getInitials = (name: string): string => {
  const trimmedName = name.trim();

  // Special handling for "Yes" and "No" outcomes
  if (trimmedName.toLowerCase() === 'yes') {
    return 'YES';
  }
  if (trimmedName.toLowerCase() === 'no') {
    return 'NO';
  }

  const parts = trimmedName.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmedName.substring(0, 2).toUpperCase();
};

/**
 * Normalizes a market's question/title by prioritizing groupItemTitle
 */
export const normalizeMarketQuestion = (market: EventMarket | Market): string => {
  const groupItemTitle = (market as any).groupItemTitle;
  if (groupItemTitle) return groupItemTitle;
  if (market?.title) return market.title;
  const question = market.question || '';
  if (question != '') return question;

  if (market?.name) return market.name;
  return 'Unknown';
};

/**
 * Normalizes a price value from string or number to number
 */
export const getPrice = (p: string | number | undefined): number => {
  if (typeof p === 'string') return parseFloat(p) || 0;
  return p || 0;
};

/**
 * Calculate the midpoint price based on bid/ask spread
 * Formula: (bestBid + bestAsk) / 2
 * Fallback to standard price if bid/ask not available
 */
export const calculateMidpointPrice = (outcome: Outcome | EventOutcome): number => {
  // Check if both bestBid and bestAsk exist as numbers
  if (typeof outcome.bestBid === 'number' && typeof outcome.bestAsk === 'number') {
    return (outcome.bestBid + outcome.bestAsk) / 2;
  }

  // Fallback to existing price property
  return getPrice(outcome.price);
};

/**
 * Fallback price for the "No" side of a binary market.
 * Uses priceNo if valid, otherwise falls back to (1 - price).
 */
export const getNoSideFallbackPrice = (priceNo: number, price: number): number => {
  if (Number.isFinite(priceNo) && priceNo > 0) return priceNo;
  return Math.max(0, 1 - price);
};

/**
 * Filter and sort markets based on specific criteria
 * Filter: active, not closed, not archived, acceptingOrders
 * Sort: Price/Percentage (descending)
 */
export function filterAndSortMarkets(markets: EventMarket[]): EventMarket[] {
  const filterMarkets = (market: EventMarket) => {
    return (
      market.active === true &&
      market.closed === false &&
      market.archived === false &&
      market.umaResolutionStatus !== 'resolved' &&
      market.acceptingOrders === true
    );
  };

  const sortByPrice = (a: EventMarket, b: EventMarket) => {
    // Primary Sort: Price (Percentage) Descending
    const { price: priceA } = resolveOutcomeData(a);
    const { price: priceB } = resolveOutcomeData(b);

    if (priceA !== priceB) {
      return priceB - priceA;
    }
  };

  return markets.filter(filterMarkets).sort(sortByPrice);
}

/**
 * Helper function to get clobTokenId from market
 * Handles various formats (string, JSON loop, array) that the backend might return
 */
export const getClobTokenId = (market: EventMarket | undefined): string => {
  if (!market) return '';
  if (market.clobTokenIds) {
    try {
      // Handle JSON string case (e.g., "[\"id1\", \"id2\"]")
      if (typeof market.clobTokenIds === 'string') {
        const parsed = JSON.parse(market.clobTokenIds);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
        return market.clobTokenIds;
      }
      // Handle array case
      if (Array.isArray(market.clobTokenIds) && market.clobTokenIds.length > 0) {
        return market.clobTokenIds[0];
      }
    } catch (e) {
      // If parsing fails, treat as regular string
      if (typeof market.clobTokenIds === 'string') {
        return market.clobTokenIds;
      }
    }
  }
  // Fallback to conditionId or id if clobTokenIds not available
  return market.conditionId || market.id || '';
};
/**
 * Determines if an event should be considered "Live"
 * For Crypto: Checks for active status, order acceptance, and current time within start/end range
 * For others: Defaults to the isLive flag from the API
 */
export function isEventLive(event: EventResponse | undefined | null, category?: string): boolean {
  if (!event) return false;

  if (category === 'crypto') {
    const firstMarket = event.markets?.[0];
    const now = new Date();
    const startTime = event.startDate ? new Date(event.startDate) : new Date(0);
    const endDate = event.endDate ? new Date(event.endDate) : new Date(8640000000000000);

    return (
      firstMarket?.acceptingOrders === true &&
      (event as any).active === true &&
      (event as any).closed === false &&
      now >= startTime &&
      now < endDate
    );
  }

  return !!event.isLive;
}
